import { API } from '../constants/apiEndpoints';
import { apiUrls } from '../utils/apiUtils';
import { axiosInstance } from '../utils/fetcher';

// Interface for credit wallet creation data
export interface CreateCreditWalletData {
  name: string;
  balance: number;
  statementDateInMonth: number;
  paymentDueDateInMonth: number;
  isInstallment?: boolean;
  installments?: Array<CreateInstallmentData>;
}

// Interface for loan wallet creation data
export interface CreateLoanWalletData {
  name: string;
  phoneNumber?: string;
  email?: string;
  isInteractionEnabled?: boolean;
}

// Interface for API response from getWallets
export interface WalletApiResponse {
  data: {
    BASIC?: string[];
    CREDIT?: string[];
    LOAN?: string[];
    [key: string]: string[] | undefined;
  };
  [key: string]: any; // For dynamically assigned wallet objects by ID
}

// Interface for installment creation data
export interface CreateInstallmentData {
  numberOfInstallments: number;
  conversionAmount: number;
  conversionDate: string;
  installmentFee?: number;
  description?: string;
}

// Service for wallet-related API operations
const WalletService = {
  // Create a new credit wallet
  createCreditWallet: async (data: CreateCreditWalletData): Promise<Wallet> => {
    const response = await axiosInstance.post(API.WALLETS.CREATE_CREDIT, data);
    return response.data;
  },

  createBasicWallet: async (payload?: any): Promise<any> => {
    const response = await axiosInstance.post(API.WALLETS.CREATE_BASIC, payload);
    return response.data;
  },

  createLoanWallet: async (data: CreateLoanWalletData): Promise<Wallet> => {
    const response = await axiosInstance.post('/wallets/loan', data);
    return response.data;
  },

  // Get all wallets
  getWallets: async (): Promise<WalletApiResponse> => {
    const response = await axiosInstance.get(API.WALLETS.BASE);
    return response.data;
  },

  // Get wallet by ID
  getWalletById: async (id: string): Promise<Wallet> => {
    const response = await axiosInstance.get(apiUrls.walletById(id));
    return response.data;
  },

  createWallet: async (wallet: Omit<Wallet, 'id' | 'userId' | 'createdAt'>) => {
    const { data } = await axiosInstance.post(API.WALLETS.BASE, wallet);
    return data;
  },

  updateWallet: async (id: string, wallet: Partial<Wallet>, type: 'BASIC' | 'CREDIT' | 'LOAN' = 'CREDIT') => {
    const url = apiUrls.editWallet(id, type);
    const { data } = await axiosInstance.put(url, wallet);

    return data;
  },

  adjustBalance: async (id: string, data: { newBalance: number }, type: 'BASIC' | 'CREDIT' = 'CREDIT') => {
    const { data: response } = await axiosInstance.put(apiUrls.adjustBalance(id, type), data);
    return response;
  },

  archiveWallet: async (id: string, type: 'BASIC' | 'CREDIT' | 'LOAN' = 'CREDIT') => {
    const { data } = await axiosInstance.put(apiUrls.archiveWallet(id, type));
    return data;
  },

  deleteWallet: async (id: string, type: 'BASIC' | 'CREDIT' | 'LOAN' = 'CREDIT') => {
    await axiosInstance.delete(apiUrls.deleteWallet(id, type));
  },

  createInstallment: async (walletId: string, data: CreateInstallmentData) => {
    const { data: response } = await axiosInstance.post(apiUrls.createInstallment(walletId), data);
    return response;
  },

  updateInstallment: async (walletId: string, installmentId: string, data: CreateInstallmentData) => {
    const { data: response } = await axiosInstance.put(apiUrls.updateInstallment(walletId, installmentId), data);
    return response;
  },

  updateWalletOrder: async (walletIds: string[]) => {
    const { data } = await axiosInstance.put(API.WALLETS.UPDATE_ORDER, {
      walletIds,
    });
    return data;
  },
};

export default WalletService;